// CreateMemberJoinHost.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberJoinHost.module.scss";
import { JOIN_HOST } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q12) */
import mrImg from "@/assets/UI/memberCreate/q18Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q18Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q18MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q18MrsM.webp";

/* ---- Framer animations (same feel as EventAttract) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta */
const JOIN_HOST_META = {
  every_weekend: "🔥",
  once_twice_month: "💃",
  every_few_months: "🕯️",
  occasionally: "🌙",
  rarely: "🧊",
  never: "💫",
};

/* Fallback English labels */
const JOIN_HOST_LABELS = {
  every_weekend: "Every weekend",
  once_twice_month: "Once or twice a month",
  every_few_months: "Every few months",
  occasionally: "Occasionally",
  rarely: "Rarely",
  never: "Never",
};

/* Closing text key helper */
function getClosingKey(rawGender, isCouple) {
  if (isCouple) return "onboarding.join_host.closing.couple";
  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.join_host.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.join_host.closing.woman";
  }
  return "onboarding.join_host.closing.other";
}

export default function CreateMemberJoinHost() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoice, setMemberChoice] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.join_host.title", {
    defaultValue: "How often do you want to join or host",
  });

  const intro1 = t("onboarding.join_host.intro1", {
    defaultValue: "This helps us match you with people who move at your pace.",
  });
  const intro2 = t("onboarding.join_host.intro2", {
    defaultValue: "Pick the rhythm that actually feels like you.",
  });

  const singleLead = t("onboarding.join_host.single.lead", {
    defaultValue: "Be honest… what feels right for your lifestyle lately",
  });
  const singleDetail = t("onboarding.join_host.single.detail", {
    defaultValue: "No judgment. We just want your matches to make sense.",
  });

  const coupleLead = t("onboarding.join_host.couple.lead", {
    defaultValue: "For couples, we’ll use one shared rhythm for both of you.",
  });
  const coupleDetail = t("onboarding.join_host.couple.detail", {
    defaultValue: "You can always change it later if your vibe shifts.",
  });

  const guideTitle = t("onboarding.join_host.guide.title", {
    defaultValue: "Quick note",
  });
  const guideBody = t("onboarding.join_host.guide.body", {
    defaultValue:
      "This doesn’t lock you in. It just tunes your suggestions so invites feel realistic, not random.",
  });

  const baseOptions =
    Array.isArray(JOIN_HOST) && JOIN_HOST.length
      ? JOIN_HOST
      : Object.keys(JOIN_HOST_LABELS);

  const options = baseOptions.map((value) => ({
    value,
    emoji: JOIN_HOST_META[value] || "✨",
    title: t(`onboarding.join_host.options.${value}.label`, {
      defaultValue: JOIN_HOST_LABELS[value] || value,
    }),
    hint: t(`onboarding.join_host.tapToSelect.${value}`, {
      defaultValue: "Tap to add this to your event vibe",
    }),
  }));

  const closingKey = memberChoice ? getClosingKey(rawGender, isCouple) : null;
  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  const submitDisabled = submitting || !memberChoice;

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoice) {
      toast.info(
        t("onboarding.join_host.toast.member_required", {
          defaultValue: "Pick one option to continue.",
        }),
      );
      return;
    }

    // If couple, send same value for member & partner
    const payload = isCouple
      ? { memberJoinHost: memberChoice, partnerJoinHost: memberChoice }
      : { memberJoinHost: memberChoice };

    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/member-create/join_host", payload, {
        skipToast: true,
      });

      await refreshMe();
      navigate(data?.next || "/member/real_image", { replace: true });
    } catch (err) {
      console.error("update join_host failed:", err);
      toast.error(
        t("onboarding.join_host.toast.save_error", {
          defaultValue: "We couldn’t save this. Please try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              <div className={cls.formSub}>{intro1}</div>
              <div className={cls.formSub}>{intro2}</div>

              {!isCouple ? (
                <>
                  <div className={cls.formSub} style={{ marginTop: 8 }}>
                    {singleLead}
                  </div>
                  <div className={cls.formSub}>{singleDetail}</div>
                </>
              ) : (
                <>
                  <div className={cls.formSub} style={{ marginTop: 8 }}>
                    {coupleLead}
                  </div>
                  <div className={cls.formSub}>{coupleDetail}</div>
                </>
              )}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            <div className={cls.block}>
              <div className={cls.blockTitle}>
                {t("onboarding.join_host.block.title", {
                  defaultValue: "Choose one that fits you best",
                })}
              </div>

              <fieldset className={cls.options}>
                <legend className="sr-only">{title}</legend>

                {options.map((opt) => {
                  const checked = memberChoice === opt.value;

                  return (
                    <label
                      key={opt.value}
                      className={`${cls.option} ${
                        checked ? cls.optionActive : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="memberJoinHost"
                        value={opt.value}
                        checked={checked}
                        onChange={() => setMemberChoice(opt.value)}
                      />

                      <span className={cls.optionBody}>
                        <span className={cls.optionEmoji} aria-hidden="true">
                          {opt.emoji}
                        </span>

                        <span className={cls.optionText}>
                          <strong>{opt.title}</strong>
                          <small>{opt.hint}</small>
                        </span>

                        <span className={cls.optionDot} aria-hidden="true" />
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            </div>

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
